'use client';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@components/ui/sheet';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { useQuery, useZero } from '@rocicorp/zero/react';
import { FC, FormEvent, useState } from 'react';
import Link from 'next/link';
import { formatQuantity } from '@utils/string-utils';
import { runZeroMutation } from '~/src/zero/run-mutation';
import { useSheetStackParams } from '@hooks/use-sheet-stack-params';
import { queries } from '~/src/zero/queries';

type IngredientSheetProps = {
  organization_id: string;
};

/**
 * Stacked above the recipe drawer (Radix Dialog, not vaul -- nesting two
 * vaul drawers is unreliable, see the plan's edge case 5). Self-sufficient:
 * reads recipe/ingredient state from the URL itself rather than a prop, so
 * it works whether it was opened from inside a recipe (editable quantity)
 * or standalone from search (read-only, ingredient may have many parent
 * recipes so there's no single quantity to edit -- edge case 4).
 */
const IngredientSheet: FC<IngredientSheetProps> = ({ organization_id }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const z = useZero() as any;
  const { recipeId, ingredientId, closeIngredient } = useSheetStackParams();
  const [saving, setSaving] = useState(false);

  const [recipeRowUntyped] = useQuery(
    recipeId && ingredientId
      ? queries.recipes.withIngredients({ recipeId })
      : undefined
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recipeRow = recipeRowUntyped as any;
  const link = recipeRow?.ingredientLinks?.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (l: any) => l.id === ingredientId
  );

  const [standaloneIngredient] = useQuery(
    !recipeId && ingredientId
      ? queries.ingredients.byId({ ingredientId })
      : undefined
  );

  const open =
    !!ingredientId && (!!link || (!recipeId && !!standaloneIngredient));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!link) {
      return;
    }
    const data = new FormData(e.currentTarget);
    const quantityRaw = (data.get('quantityRaw') as string) ?? '';
    if (!quantityRaw.trim()) {
      return;
    }
    setSaving(true);
    await runZeroMutation(
      z.mutate.recipeIngredients.update({
        id: link.id,
        quantityRaw,
        now: Date.now()
      }),
      { errorTitle: 'Could not save ingredient' }
    );
    setSaving(false);
  };

  const title = link
    ? link.ingredient?.fullName
    : (standaloneIngredient as any)?.fullName;
  const brand = link
    ? link.ingredient?.brandName
    : (standaloneIngredient as any)?.brandName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const usedInRecipes = ((standaloneIngredient as any)?.recipeLinks ?? [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((rl: any) => rl.recipe)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((r: any) => !!r);

  return (
    <Sheet
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          closeIngredient();
        }
      }}
    >
      <SheetContent side="bottom" className="mx-auto max-w-md rounded-t-xl">
        <SheetHeader>
          <SheetTitle>{title ?? 'Ingredient'}</SheetTitle>
          {brand ? <SheetDescription>{brand}</SheetDescription> : null}
        </SheetHeader>

        {link ? (
          <form
            key={link.id}
            onSubmit={handleSubmit}
            className="flex items-end gap-2 pt-4"
          >
            <div className="flex-1">
              <label
                htmlFor="quantityRaw"
                className="mb-1 block text-label text-muted-foreground"
              >
                Quantity
              </label>
              <Input
                id="quantityRaw"
                name="quantityRaw"
                defaultValue={formatQuantity(
                  link.quantityStr ?? '',
                  link.quantityType ?? ''
                )}
                placeholder="e.g. 2 oz"
              />
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </form>
        ) : standaloneIngredient ? (
          <div className="pt-4">
            <p className="mb-2 text-body-dense text-muted-foreground">
              This ingredient is used in {usedInRecipes.length || 'multiple'}{' '}
              recipe{usedInRecipes.length === 1 ? '' : 's'}. Open a recipe to
              edit its quantity there.
            </p>
            <ul className="divide-y divide-border rounded-md border">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {usedInRecipes.map((recipe: any) => (
                <li key={recipe.id}>
                  <Link
                    href={`/${organization_id}/bev/recipes/${recipe.id}`}
                    className="flex min-h-11 items-center p-3 text-body-dense hover:bg-accent"
                    onClick={() => closeIngredient()}
                  >
                    {recipe.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export default IngredientSheet;
